import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { format } from 'date-fns';

const AssignedWorkOrders = ({ assignments, onAccept, onStart, onComplete, onSelectWorkOrder }) => {
  const [filter, setFilter] = useState('all');

  const getStatusConfig = (status) => {
    const configs = {
      assigned: { label: 'Assigned', color: 'text-warning', bg: 'bg-warning/10', icon: 'Clock' },
      accepted: { label: 'Accepted', color: 'text-primary', bg: 'bg-primary/10', icon: 'CheckCircle' },
      in_progress: { label: 'In Progress', color: 'text-success', bg: 'bg-success/10', icon: 'Loader' },
      completed: { label: 'Completed', color: 'text-muted-foreground', bg: 'bg-muted', icon: 'CheckCircle2' }
    };
    return configs?.[status] || configs?.assigned;
  };

  const getPriorityConfig = (priority) => {
    const configs = {
      low: { label: 'Low', color: 'text-muted-foreground', bg: 'bg-muted' },
      medium: { label: 'Medium', color: 'text-primary', bg: 'bg-primary/10' },
      high: { label: 'High', color: 'text-warning', bg: 'bg-warning/10' },
      urgent: { label: 'Urgent', color: 'text-error', bg: 'bg-error/10' }
    };
    return configs?.[priority] || configs?.medium;
  };

  const filteredAssignments = filter === 'all'
    ? assignments
    : assignments?.filter(a => a?.status === filter);

  return (
    <div className="space-y-4">
      {/* Filter Buttons */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2">
        {['all', 'assigned', 'accepted', 'in_progress']?.map((status) => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              filter === status
                ? 'bg-primary text-primary-foreground'
                : 'bg-card border border-border text-foreground hover:bg-muted'
            }`}
          >
            {status === 'all' ? 'All' : status?.replace('_', ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}
          </button>
        ))}
      </div>

      {/* Work Orders List */}
      {filteredAssignments?.length === 0 ? (
        <div className="bg-card rounded-xl border border-border p-12 text-center">
          <Icon name="ClipboardList" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No Work Orders</h3>
          <p className="text-sm text-muted-foreground">You have no {filter !== 'all' ? filter : ''} work orders at this time.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredAssignments?.map((assignment) => {
            const workOrder = assignment?.work_order;
            const statusConfig = getStatusConfig(assignment?.status);
            const priorityConfig = getPriorityConfig(workOrder?.priority);

            return (
              <div
                key={assignment?.id}
                className="bg-card rounded-xl border border-border overflow-hidden hover:shadow-lg transition-shadow"
              >
                <div className="p-4">
                  {/* Header */}
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusConfig?.bg} ${statusConfig?.color}`}>
                          <Icon name={statusConfig?.icon} size={12} />
                          {statusConfig?.label}
                        </span>
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${priorityConfig?.bg} ${priorityConfig?.color}`}>
                          {priorityConfig?.label}
                        </span>
                      </div>
                      <h3 className="text-base font-semibold text-foreground mb-1 truncate">
                        {workOrder?.title}
                      </h3>
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {workOrder?.description}
                      </p>
                    </div>
                  </div>

                  {/* Equipment Info */}
                  <div className="flex items-center gap-4 mb-3 text-sm">
                    <div className="flex items-center gap-1.5 text-muted-foreground">
                      <Icon name="Package" size={14} />
                      <span>{workOrder?.equipment?.name}</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-muted-foreground">
                      <Icon name="MapPin" size={14} />
                      <span>{workOrder?.equipment?.location}</span>
                    </div>
                  </div>

                  {/* Scheduled Date */}
                  {workOrder?.scheduled_date && (
                    <div className="flex items-center gap-1.5 text-sm text-muted-foreground mb-3">
                      <Icon name="Calendar" size={14} />
                      <span>Scheduled: {format(new Date(workOrder?.scheduled_date), 'MMM dd, yyyy')}</span>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex items-center gap-2 pt-3 border-t border-border">
                    {assignment?.status === 'assigned' && (
                      <>
                        <Button
                          variant="primary"
                          size="sm"
                          iconName="CheckCircle"
                          iconPosition="left"
                          onClick={() => onAccept(assignment?.id)}
                          className="flex-1"
                        >
                          Accept
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          iconName="Eye"
                          onClick={() => onSelectWorkOrder(workOrder)}
                        >
                          View
                        </Button>
                      </>
                    )}

                    {assignment?.status === 'accepted' && (
                      <>
                        <Button
                          variant="primary"
                          size="sm"
                          iconName="Play"
                          iconPosition="left"
                          onClick={() => onStart(assignment?.id)}
                          className="flex-1"
                        >
                          Start Work
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          iconName="Eye"
                          onClick={() => onSelectWorkOrder(workOrder)}
                        >
                          View
                        </Button>
                      </>
                    )}

                    {assignment?.status === 'in_progress' && (
                      <>
                        <Button
                          variant="primary"
                          size="sm"
                          iconName="CheckCircle2"
                          iconPosition="left"
                          onClick={() => onComplete(assignment?.id)}
                          className="flex-1"
                        >
                          Complete
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          iconName="Eye"
                          onClick={() => onSelectWorkOrder(workOrder)}
                        >
                          Details
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AssignedWorkOrders;